import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'What types of home service businesses does this work for?',
    answer: 'Our AI automation platform works for virtually any home service trade — general contractors, house cleaners, landscapers, HVAC technicians, plumbers, electricians, painters, handymen, pest control, and more. If customers call or message you to book a job, our AI handles it.',
  },
  {
    question: 'How does the AI know what to say about my business?',
    answer: 'We train your AI on your specific business — your services, service area, pricing, scheduling availability, and FAQs. It answers caller questions accurately and professionally, just like a knowledgeable receptionist who knows your business inside and out.',
  },
  {
    question: 'Can the AI handle after-hours calls from homeowners?',
    answer: 'Yes — that\'s one of its biggest strengths. Your AI receptionist is live 24/7, so when a homeowner calls at 9pm about a broken AC or a backed-up drain, your AI answers, gathers the details, and books a service appointment right then and there.',
  },
  {
    question: 'Does it integrate with my existing calendar or scheduling tool?',
    answer: 'Yes. Our AI integrates with your calendar and checks real-time availability before confirming any booking. No double-bookings, no gaps, no confusion.',
  },
  {
    question: 'What if a customer has an emergency?',
    answer: 'Your AI can be trained to flag and escalate urgent situations — water leaks, HVAC failures, electrical issues — by notifying you immediately via text or call while simultaneously helping the homeowner with next steps.',
  },
  {
    question: 'How fast can I get up and running?',
    answer: 'Most home service businesses are fully live within 24–48 hours. Our team handles setup, training, and testing from start to finish. You don\'t need any technical experience.',
  },
];

const painPoints = [
  {
    icon: 'ri-phone-line',
    title: 'Calls Missed During Jobs',
    desc: 'You\'re in a crawl space, on a roof, or running a crew. Your phone rings. You can\'t answer. That homeowner calls the next company on Google and you never knew.',
  },
  {
    icon: 'ri-moon-line',
    title: 'Leads Lost After Hours',
    desc: 'Homeowners search for help evenings and weekends. Without someone to answer, you\'re handing free leads to competitors who are ready to respond.',
  },
  {
    icon: 'ri-calendar-close-line',
    title: 'Scheduling Takes Too Much Time',
    desc: 'Texting and calling back and forth to confirm appointment times is a time drain. Every minute spent scheduling is a minute not spent on revenue-generating work.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'No Consistent Follow-Up System',
    desc: 'Leads go cold fast. Without automatic follow-up, potential customers forget about you and move on to whoever reached back out first.',
  },
];

const solutions = [
  {
    icon: 'ri-phone-line',
    title: 'AI Phone Answering — 24/7',
    desc: 'Every call gets answered immediately, professionally, and intelligently — whether it\'s 8am or midnight on a Saturday. No voicemail, no missed opportunities.',
    link: '/services/ai-receptionist',
  },
  {
    icon: 'ri-chat-smile-3-line',
    title: 'AI Website Chatbot',
    desc: 'Your website becomes a lead-generating machine. The AI chatbot engages visitors, qualifies their needs, and books them directly into your schedule.',
    link: '/services/ai-chatbot',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Automated Job Scheduling',
    desc: 'Customers book their own service appointments — your AI checks calendar availability in real time and confirms the slot instantly. Zero phone tag.',
    link: '/services/appointment-scheduling',
  },
  {
    icon: 'ri-mail-send-line',
    title: 'Automated Follow-Up',
    desc: 'AI sends timely follow-up messages to every new lead, unbooked inquiry, and estimate ghost. Close more jobs without doing any of the manual chasing.',
    link: null,
  },
];

const benefits = [
  { icon: 'ri-flashlight-line', title: 'Never Miss a Lead', desc: 'AI answers every call and message 24/7 — including after-hours, weekends, and holidays.' },
  { icon: 'ri-briefcase-4-line', title: 'Fill Your Calendar Automatically', desc: 'Customers book service appointments themselves while you focus on the work in front of you.' },
  { icon: 'ri-time-line', title: 'Reclaim Hours Every Week', desc: 'Stop wasting time on scheduling calls, voicemail check-ins, and repetitive customer questions.' },
  { icon: 'ri-trophy-line', title: 'Win More Jobs Than Competitors', desc: 'The fastest response wins. AI for home services means you respond to every lead before your competitor even sees their missed call.' },
];

const steps = [
  {
    icon: 'ri-settings-4-line',
    title: 'We Set Up Your System',
    desc: 'Our team builds and trains your AI on your business — services, pricing, service area, FAQs, and calendar. You\'re live in 24–48 hours with zero tech work on your end.',
  },
  {
    icon: 'ri-robot-line',
    title: 'AI Handles Calls & Bookings',
    desc: 'Your AI receptionist answers every call, responds to website visitors, and books service appointments automatically — around the clock, every day of the week.',
  },
  {
    icon: 'ri-hand-coin-line',
    title: 'You Show Up & Close More Jobs',
    desc: 'Walk into every appointment with a pre-qualified customer. Your pipeline is full, your calendar is organized, and your revenue grows without extra overhead.',
  },
];

const trades = [
  {
    icon: 'ri-home-8-line',
    title: 'General Contractors',
    desc: 'Remodels, additions, and repairs — your AI captures estimate leads 24/7 and books walkthroughs directly to your calendar.',
    link: '/ai-for-contractors',
    img: '/readdy-assets/hs-trade-contractor-001.jpg',
  },
  {
    icon: 'ri-magic-line',
    title: 'House Cleaners',
    desc: 'Book recurring cleans, one-time deep cleans, and move-in/out appointments automatically — your AI fills your slots while you focus on the next job.',
    link: null,
    img: '/readdy-assets/hs-trade-cleaner-001.jpg',
  },
  {
    icon: 'ri-plant-line',
    title: 'Landscapers',
    desc: 'Spring and fall rushes are no problem — AI books lawn care, cleanups, and landscaping consultations while your crews are in the field.',
    link: '/ai-for-landscapers',
    img: '/readdy-assets/hs-trade-landscaper-001.jpg',
  },
  {
    icon: 'ri-temp-hot-line',
    title: 'HVAC Companies',
    desc: 'Peak season demand and emergency calls handled instantly. AI answers, qualifies the urgency, and dispatches your team — day or night.',
    link: null,
    img: '/readdy-assets/hs-trade-hvac-001.jpg',
  },
  {
    icon: 'ri-drop-line',
    title: 'Plumbers',
    desc: 'Emergency or routine — every call gets answered. AI captures the issue, books the appointment, and notifies your team so nobody waits.',
    link: null,
    img: '/readdy-assets/hs-trade-plumber-001.jpg',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Electricians',
    desc: 'Panel upgrades, outlet repairs, new installs — AI books qualified service calls and filters out tire-kickers so your calendar is full of real jobs.',
    link: null,
    img: '/readdy-assets/hs-trade-electrician-001.jpg',
  },
  {
    icon: 'ri-brush-line',
    title: 'Painters',
    desc: 'Capture quote requests and book estimate appointments automatically — even while your crew is on a job site. Never miss a residential or commercial painting lead.',
    link: '/industries/ai-for-painters',
    img: '/readdy-assets/hs-trade-painter-001.jpg',
  },
  {
    icon: 'ri-tools-line',
    title: 'Appliance Repair',
    desc: 'Answer every service call 24/7 — including emergency repair requests. AI captures appliance details, books the appointment, and keeps your schedule full.',
    link: '/industries/ai-for-appliance-repair',
    img: '/readdy-assets/hs-trade-appliance-001.jpg',
  },
  {
    icon: 'ri-scissors-line',
    title: 'Salons & Spas',
    desc: 'Book appointments 24/7, send automated reminders, and reduce no-shows — AI for beauty businesses keeps your calendar full without interrupting your team.',
    link: '/industries/ai-for-salons-spas',
    img: '/readdy-assets/hs-trade-salon-001.jpg',
  },
];

export default function AIForHomeServices() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="AI for Home Services | Automation for Home Service Companies | Dino Nuggets"
        description="AI for home services that answers every call, books more jobs, and automates follow-up 24/7. Automation for home service companies — contractors, cleaners, HVAC, plumbers, landscapers, painters, and more."
        canonical="/industries/ai-for-home-services"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Automation for Home Service Businesses',
              description: 'AI phone answering, automated scheduling, and lead follow-up built for contractors, cleaners, landscapers, HVAC, plumbers, painters, appliance repair, and all home service trades.',
              provider: { '@type': 'Organization', name: 'Dino Nuggets' },
              areaServed: 'United States',
              keywords: 'AI for home services, AI for service businesses, automation for home service companies, AI phone answering home services',
            },
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
          },
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Spring Sale Banner */}
        <div className="relative bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-white rounded-full translate-x-1/4 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-1/3 w-24 h-24 bg-white rounded-full translate-y-1/2"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6 py-3.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
            <div className="flex items-center gap-2.5">
              <span className="text-lg">🌱</span>
              <span className="font-bold text-base tracking-wide uppercase">Spring Sale</span>
              <span className="hidden sm:block w-px h-5 bg-white/40"></span>
            </div>
            <p className="text-sm sm:text-base font-medium text-white/95">
              <strong className="font-bold text-white">No setup fees</strong> on all automation packages through April 30th — save up to $500 on getting started.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="flex-shrink-0 bg-white text-emerald-600 hover:bg-emerald-50 px-4 py-1.5 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer"
            >
              Claim Offer
            </button>
          </div>
        </div>

        {/* ── HERO ─────────────────────────────────────────────────── */}
        <section className="relative pt-28 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/homeservices-hero-001.jpg"
              alt="AI automation for home service businesses"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white to-white/10"></div>

          <div className="relative max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6 tracking-wide uppercase">
                Home Service AI
              </span>
              <h1 className="font-display text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
                AI Automation for<br />
                <span className="text-primary">Home Service Businesses</span>
              </h1>
              <p className="text-xl text-gray-700 mb-10 leading-relaxed">
                Stop missing calls, book more jobs, and automate your entire front desk — built for contractors, cleaners, landscapers, HVAC, plumbers, and every trade in between.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-lg"
                >
                  Book a Free Consultation
                </button>
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="border-2 border-gray-300 hover:border-primary hover:text-primary text-gray-700 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-lg"
                >
                  See How It Works
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-6 mt-8">
                {['Live in 24–48 hours', 'No tech skills needed', 'Cancel anytime'].map(t => (
                  <div key={t} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary text-lg"></i>
                    </div>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── STATS BAR ────────────────────────────────────────────── */}
        <section className="bg-secondary py-10 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {[
              { stat: '70%', label: 'of callers hang up rather than leave a voicemail — and call your competitor instead' },
              { stat: '5 min', label: 'response window before a lead\'s chance of booking drops by 80%' },
              { stat: '3×', label: 'more jobs closed by home service businesses that respond instantly to inquiries' },
            ].map(item => (
              <div key={item.stat} className="flex flex-col items-center">
                <span className="text-4xl font-extrabold text-primary mb-2">{item.stat}</span>
                <p className="text-gray-400 text-sm leading-relaxed max-w-xs">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── PAIN POINTS ──────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-red-50 text-red-500 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Sound Familiar?</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                The Problems Every Home Service Business Faces
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                Whether you clean homes, fix AC units, or build decks as a <Link to="/ai-for-contractors" className="text-primary hover:underline font-medium">contractor</Link> — these bottlenecks are draining your revenue. AI for home services was built to eliminate every one of them.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {painPoints.map(item => (
                <div key={item.title} className="bg-white p-7 rounded-xl border border-gray-200 hover:border-red-200 transition-all group">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg mb-5 group-hover:bg-red-100 transition-colors">
                    <i className={`${item.icon} text-2xl text-red-500`}></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SOLUTION ─────────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">The Solution</span>
                <h2 className="font-display text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  How Dino Nuggets Automates Your Home Service Business
                </h2>
                <p className="text-gray-700 leading-relaxed mb-5">
                  Our platform combines <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI phone answering</Link>, an <Link to="/services/ai-chatbot" className="text-primary hover:underline font-medium">intelligent website chatbot</Link>, and <Link to="/services/appointment-scheduling" className="text-primary hover:underline font-medium">automated scheduling</Link> — one complete system that works 24/7 so you don't have to.
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  AI for home services isn't just answering phones — it's a fully trained virtual front desk that knows your business, speaks to your customers professionally, books jobs to your calendar, and follows up automatically with every lead you generate.
                </p>
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="bg-primary hover:bg-primary/90 text-white px-7 py-3.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  Book a Free Consultation
                </button>
              </div>
              <div className="space-y-5">
                {solutions.map(item => (
                  <div key={item.title} className="flex gap-5 p-6 bg-gray-50 rounded-xl hover:bg-primary/5 transition-colors group">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg mb-1.5">
                        {item.link ? (
                          <Link to={item.link} className="hover:text-primary transition-colors cursor-pointer">{item.title}</Link>
                        ) : item.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── BENEFITS ─────────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-secondary to-secondary/90 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-white/10 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Why It Works</span>
              <h2 className="font-display text-4xl font-bold text-white mb-4">
                What Changes When AI Runs Your Front Desk
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
                From your first call every morning to the last inquiry on Sunday night — your AI is handling it all.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map(item => (
                <div key={item.title} className="bg-white/5 border border-white/10 p-7 rounded-xl hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/20 rounded-lg mb-5">
                    <i className={`${item.icon} text-2xl text-primary`}></i>
                  </div>
                  <h3 className="font-semibold text-white text-lg mb-3">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ─────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Simple Process</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                3 Steps to a Fully Automated Home Service Business
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                No complicated setup, no technical skills required. We handle everything so you can focus on the work.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-10 relative">
              <div className="hidden md:block absolute top-16 left-1/3 right-1/3 h-px bg-gradient-to-r from-primary/30 via-primary to-primary/30"></div>
              {steps.map((step, idx) => (
                <div key={step.title} className="relative text-center">
                  <div className="relative inline-flex">
                    <div className="w-16 h-16 flex items-center justify-center bg-primary rounded-2xl mb-6 mx-auto shadow-lg">
                      <i className={`${step.icon} text-3xl text-white`}></i>
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center bg-secondary text-white text-xs font-bold rounded-full border-2 border-white">
                      {idx + 1}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-xl text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-lg"
              >
                Book a Free Consultation
              </button>
            </div>
          </div>
        </section>

        {/* ── TRADES ───────────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">Trades We Serve</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                AI Built for Every Home Service Trade
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                From residential cleaning crews to HVAC fleets — our AI adapts to your specific trade, workflow, and customer type. See how <Link to="/industries/ai-for-appliance-repair" className="text-primary hover:underline font-medium">appliance repair technicians</Link> and <Link to="/industries/ai-for-painters" className="text-primary hover:underline font-medium">painters</Link> use the same platform.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trades.map(item => (
                <div key={item.title} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-primary hover:shadow-md transition-all group">
                  <div className="h-48 overflow-hidden">
                    <img
                      src={item.img}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-xl text-primary`}></i>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {item.link ? (
                          <Link to={item.link} className="hover:text-primary transition-colors cursor-pointer">{item.title}</Link>
                        ) : item.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                    {item.link && (
                      <Link
                        to={item.link}
                        className="inline-flex items-center gap-1.5 text-primary text-sm font-medium mt-4 hover:underline cursor-pointer"
                      >
                        Learn more <i className="ri-arrow-right-line"></i>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SERVICES GRID ────────────────────────────────────────── */}
        <section className="py-16 px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-primary/5 rounded-2xl p-10">
              <div className="text-center mb-10">
                <h2 className="font-display text-3xl font-bold text-gray-900 mb-3">
                  Our Full Suite of AI Tools for Home Services
                </h2>
                <p className="text-gray-600 leading-relaxed max-w-xl mx-auto">
                  Mix and match the AI tools that fit your business. Every tool is purpose-built for field service operations.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { to: '/services/ai-receptionist', icon: 'ri-customer-service-2-line', title: 'AI Receptionist', desc: 'Full virtual front desk — calls, texts, and web chat handled around the clock.' },
                  { to: '/ai-phone-answering-service', icon: 'ri-phone-line', title: 'AI Phone Answering', desc: 'Live-sounding AI that picks up every call and handles it professionally.' },
                  { to: '/ai-appointment-scheduling', icon: 'ri-calendar-2-line', title: 'Appointment Scheduling', desc: 'Customers book directly into your calendar with zero manual effort.' },
                  { to: '/services/ai-chatbot', icon: 'ri-chat-smile-3-line', title: 'AI Website Chatbot', desc: 'Capture and convert website visitors into booked jobs 24/7.' },
                ].map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="bg-white p-5 rounded-xl border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                  >
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1 text-sm">{item.title}</h3>
                      <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ─────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-3">
                Real Results from Home Service Businesses
              </h2>
              <p className="text-gray-600">What happens when your AI front desk goes live.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  quote: 'I run a cleaning company and used to miss calls constantly during jobs. Now the AI books new clients while my team cleans. It paid for itself in the first week.',
                  name: 'Priya M.',
                  role: 'Residential Cleaning Company, Arizona',
                },
                {
                  quote: 'As a plumber, I can\'t answer my phone when I\'m under a sink. The AI handles every call professionally and books the appointment. My revenue is up 30% this quarter.',
                  name: 'Dale K.',
                  role: 'Plumbing Business Owner, Ohio',
                },
                {
                  quote: 'My HVAC company gets slammed in summer. The AI for home services handles the surge — answered 47 calls in one weekend I couldn\'t have managed manually.',
                  name: 'Rita F.',
                  role: 'HVAC Company Owner, Tennessee',
                },
              ].map(t => (
                <div key={t.name} className="bg-white p-7 rounded-xl border border-gray-200">
                  <div className="flex gap-1 mb-4">
                    {[1,2,3,4,5].map(s => (
                      <i key={s} className="ri-star-fill text-yellow-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">&ldquo;{t.quote}&rdquo;</p>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────── */}
        <section className="py-20 px-6 lg:px-8 bg-white">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">FAQ</span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">
                Questions About AI for Home Services
              </h2>
              <p className="text-gray-600 leading-relaxed">Everything you need to know before getting started.</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5 pt-1">
                      <p className="text-gray-700 leading-relaxed text-sm">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ────────────────────────────────────────────── */}
        <section className="py-24 px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/homeservices-cta-bg-001.jpg"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-secondary/91"></div>
          </div>
          <div className="relative max-w-3xl mx-auto text-center">
            <span className="inline-block bg-primary/20 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6 uppercase tracking-wide">
              Ready to Grow?
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
              Ready to Stop Missing Home Service Leads?
            </h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed">
              Book a free 15-minute consultation and see exactly how AI for home services fits your specific trade. No commitment, no tech headaches.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white px-10 py-5 rounded-lg font-bold transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer text-lg"
            >
              Book Your Free Consultation
            </button>
            <p className="text-gray-500 text-sm mt-5">Free 15-min call · No credit card required · Live in 24–48 hours</p>
          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />

        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
